import React from "react";
import { Tooltip, makeStyles } from "@material-ui/core";
import SignalWifi4BarIcon from "@material-ui/icons/SignalWifi4Bar";
import SignalWifi2BarIcon from "@material-ui/icons/SignalWifi2Bar";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import { i18n } from "../../translate/i18n";
import useSocketStatus from "../../hooks/useSocketStatus";

const T = "mainDrawer.appBar.socketStatus";

const useStyles = makeStyles(() => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 9px 0 7px",
    margin: "0 4px",
    borderRadius: 13,
    // chip translúcido sobre a navbar colorida
    background: "rgba(255, 255, 255, 0.14)",
    color: "#fff",
    userSelect: "none",
    cursor: "default",
    lineHeight: 1,
    transition: "background 0.2s ease"
  },
  icon: {
    fontSize: 16,
    display: "block"
  },
  // pulso suave no ícone quando saudável
  pulse: {
    animation: "$pulse 2s ease-in-out infinite"
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.55 }
  },
  delay: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap"
  },
  unit: {
    fontSize: 9,
    fontWeight: 600,
    opacity: 0.7,
    marginLeft: 1
  },
  tooltip: {
    maxWidth: 260
  },
  panelTitle: {
    fontWeight: 700,
    marginBottom: 6
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "1px 0",
    fontVariantNumeric: "tabular-nums"
  },
  rowLabel: {
    opacity: 0.75
  },
  hint: {
    marginTop: 6,
    paddingTop: 5,
    borderTop: "1px solid rgba(255,255,255,0.2)",
    opacity: 0.9
  }
}));

const COLORS = {
  good: "#69f0ae",
  fair: "#ffb74d",
  bad: "#ff5252",
  unknown: "#bdbdbd"
};

// Acima disto o event loop do backend já está segurando entrega de eventos.
const LOOP_LAG_HIGH_MS = 100;

/**
 * Indicador do estado do WebSocket na navbar: ícone de sinal + RTT (ms), com
 * o detalhamento das métricas no tooltip. Diagnostica quando o tempo-real cai
 * ou degrada (#4147).
 *
 *  - verde:    conectado e rápido
 *  - amarelo:  conectado mas lento
 *  - vermelho: desconectado, ou ping sem resposta
 */
const SocketStatusIndicator = () => {
  const classes = useStyles();
  const { status, latency, metrics, quality } = useSocketStatus();

  let color = COLORS.unknown;
  let label = i18n.t(`${T}.connecting`);
  let Icon = SignalWifi2BarIcon;
  let healthy = false;

  if (status === "disconnected") {
    color = COLORS.bad;
    label = i18n.t(`${T}.disconnected`);
    Icon = SignalWifiOffIcon;
  } else if (status === "connecting") {
    color = COLORS.fair;
    label = i18n.t(`${T}.reconnecting`);
    Icon = SignalWifi2BarIcon;
  } else if (status === "connected") {
    color = COLORS[quality] || COLORS.unknown;
    if (quality === "good") {
      label = i18n.t(`${T}.connected`);
      Icon = SignalWifi4BarIcon;
      healthy = true;
    } else if (quality === "unknown") {
      label = i18n.t(`${T}.connecting`);
    } else {
      label = i18n.t(`${T}.slow`, { ms: latency ?? 0 });
      Icon = SignalWifi2BarIcon;
    }
  }

  const ms = value => `${value} ms`;

  const rows = [];
  if (metrics.rtt != null) {
    rows.push([i18n.t(`${T}.details.rtt`), ms(metrics.rtt)]);
  }
  if (metrics.networkMs != null) {
    rows.push([i18n.t(`${T}.details.network`), ms(metrics.networkMs)]);
    rows.push([i18n.t(`${T}.details.server`), ms(metrics.serverMs)]);
  }
  if (metrics.samples > 1) {
    rows.push([i18n.t(`${T}.details.jitter`), `± ${ms(metrics.jitter)}`]);
  }
  if (metrics.loopLag) {
    rows.push([i18n.t(`${T}.details.loopLag`), ms(metrics.loopLag.p99)]);
  }
  if (metrics.lost > 0) {
    rows.push([i18n.t(`${T}.details.lost`), String(metrics.lost)]);
  }
  if (metrics.reconnects > 0) {
    rows.push([i18n.t(`${T}.details.reconnects`), String(metrics.reconnects)]);
  }

  // Quem está segurando o tempo-real: o backend ou a rede do atendente? As duas
  // causas dão RTT alto e pedem ações opostas, então vale dizer qual é.
  let hint = null;
  if (quality === "fair" || quality === "bad") {
    const loopLagHigh = (metrics.loopLag?.p99 ?? 0) >= LOOP_LAG_HIGH_MS;
    const serverHeavy =
      metrics.serverMs != null && metrics.serverMs > (metrics.networkMs ?? 0);
    if (loopLagHigh || serverHeavy) {
      hint = i18n.t(`${T}.details.hintServer`);
    } else if (metrics.networkMs != null) {
      hint = i18n.t(`${T}.details.hintNetwork`);
    }
  }

  const title = (
    <div>
      <div className={classes.panelTitle}>{label}</div>
      {rows.length === 0 ? (
        <div className={classes.rowLabel}>{i18n.t(`${T}.details.waiting`)}</div>
      ) : (
        rows.map(([name, value]) => (
          <div className={classes.row} key={name}>
            <span className={classes.rowLabel}>{name}</span>
            <span>{value}</span>
          </div>
        ))
      )}
      {hint && <div className={classes.hint}>{hint}</div>}
    </div>
  );

  const showDelay = status === "connected" && latency != null;

  return (
    <Tooltip title={title} arrow classes={{ tooltip: classes.tooltip }}>
      <div className={classes.root}>
        <Icon
          className={`${classes.icon} ${healthy ? classes.pulse : ""}`}
          style={{ color }}
        />
        {showDelay && (
          <span className={classes.delay} style={{ color }}>
            {latency}
            <span className={classes.unit}>ms</span>
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export default SocketStatusIndicator;
